import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { HomeData } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { AboutSection } from '@/components/landing/about-section'
import { ContactSection } from '@/components/landing/contact-section'
import { ExperienceSection } from '@/components/landing/experience-section'
import { HeroSection } from '@/components/landing/hero-section'
import { MarqueeTapes } from '@/components/landing/marquee-tapes'
import { SkillsSection } from '@/components/landing/skills-section'
import { WorkSection } from '@/components/landing/work-section'
import { useTranslation } from '@/hooks/use-translation'
import { getHomeData } from '@/lib/portfolio'
import { resolveLocale } from '@/constants/i18n'

interface HomePageProps extends HomeData {
    year: number
}

const Home: FC<HomePageProps> = ({
    profile,
    highlights,
    projects,
    experiences,
    skills,
    tools,
    year
}): ReactNode => {
    const description = profile
        ? `Portfolio of ${profile.first_name} ${profile.last_name}${profile.role ? `, ${profile.role.toLowerCase()}` : ''}: projects, experience, skills and contact.`
        : undefined

    return (
        <Layout profile={profile} year={year} isHome>
            <Seo description={description} ogImage={profile?.portrait ?? undefined} />

            {profile ? (
                <HeroSection profile={profile} />
            ) : (
                <section id='profile' className='flex min-h-[70vh] items-center justify-center px-6 pt-24 text-center'>
                    <p className='max-w-md font-heading text-3xl font-bold'>
                        This portfolio is getting a fresh coat of paint. Come back very soon.
                    </p>
                </section>
            )}

            <MarqueeTapes />

            {profile && <AboutSection profile={profile} highlights={highlights} />}

            <WorkSection profile={profile} projects={projects} />
            <ExperienceSection profile={profile} experiences={experiences} />

            {(skills.length > 0 || tools.length > 0) && (
                <SkillsSection profile={profile} skills={skills} tools={tools} />
            )}

            <ContactSection profile={profile} />
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HomePageProps> = async () => {
    try {
        const data = await getHomeData()

        return {
            props: {
                ...data,
                year: new Date().getUTCFullYear()
            }
        }
    }
    catch (error) {
        console.error('Home page: the portfolio content could not be loaded.', error instanceof Error ? error.message : error)
        throw error
    }
}

export default Home
